import type {
  ApiError,
  ValidationErrorIssue,
  ValidationErrorMessage,
} from '@festina/life-general-types/life'
import { notEmpty, type Optional, typeUtils } from '@festina-life/core'

const formatIssue: (issue: ValidationErrorIssue) => string = (issue) => {
  const subIssues = issue?.subIssues
    ? issue?.subIssues?.filter(notEmpty).map(formatIssue)
    : []

  return `${issue.message}\n${subIssues}`
}

export const isValidationErrorMessage = (
  x: unknown,
): x is ValidationErrorMessage =>
  typeUtils.isObject(x) &&
  '_type' in x &&
  x['_type'] === 'ValidationErrorMessage'

export const formatIssues: (x: ValidationErrorMessage) => string = (issues) => {
  // TODO: Instead of just dumping the server generated issue messages, we should actually do a translation of the key that it also provides.
  const issueDescriptions = issues.issues?.filter(notEmpty)?.map(formatIssue)

  return issueDescriptions ? issueDescriptions.join('\n\n') : ''
}

export const formatInternalServerError = (error: ApiError) => {
  if (!error.body || typeof error.body != 'string') {
    // The error body is not a string, so we can't parse it.
    // Probably a validationError and we already showed it in the UI.
    return ''
  }
  // Most likely an error 500.
  // The details are returned as a JSON string (already parsed).
  // Extract the cause, which is often what has meaningful information.
  // If that fails, look for the message after the first exception.
  for (const prefix of ['Caused by:', 'Exception:']) {
    const causedByIndex = error.body.indexOf(prefix)

    if (causedByIndex !== -1) {
      return error.body
        .slice(
          causedByIndex + prefix.length,
          error.body.indexOf('\n', causedByIndex),
        )
        .trim()
    }
  }
  return error.body
}

/**
 * Utility function to merge multiple `Error`s into one.
 *
 * This is useful when multiple data sources are to be treated as one, but each could fail individually.
 *
 * When there is a single `Error`, that same instance is returned. If there is more than one,
 * all stringified versions of the errors are joined together into a single new `Error`.
 * */
export const mergeErrors = (
  ...errors: Optional<Error>[]
): Error | undefined => {
  const existingErrors = errors.filter(notEmpty)

  switch (existingErrors.length) {
    case 0:
      return undefined
    case 1:
      return existingErrors[0]
    default:
      return new Error(existingErrors.join('\n'))
  }
}
